import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { fmtCurrency } from "../lib/format";
import { TopBar } from "../components/TopBar";
import { PeriodBadge, formatPeriodDate } from "../components/PeriodBadge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Loader2, Archive } from "lucide-react";
import { toast } from "sonner";
import { cn } from "../lib/utils";

export default function HistoryPage() {
  const navigate = useNavigate();
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/history/periods");
      setPeriods(res.data);
    } catch (e) {
      toast.error("Failed to load weekly history");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="min-h-screen bg-muted/30">
      <TopBar crumbs={[{ label: "Weekly History" }]} />
      <main className="mx-auto max-w-7xl space-y-6 px-4 py-8 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">Weekly History</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Closed accounting periods. Historical data is read-only.
            </p>
          </div>
          <PeriodBadge />
        </div>

        {loading ? (
          <div className="flex h-40 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : periods.length === 0 ? (
          <Card className="shadow-sm">
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <Archive className="h-8 w-8 text-muted-foreground" />
              <p className="mt-3 text-sm font-medium">No archived weeks yet</p>
              <p className="mt-1 max-w-md text-xs text-muted-foreground">
                Every Tuesday the current week is automatically closed and archived here. You can also close a
                week manually with the "Reset Week" button on the home page.
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card className="shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold">Archived Weeks</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="w-full overflow-x-auto rounded-md border border-border">
                <Table data-testid="history-periods-table" className="min-w-[900px]">
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead>Week start</TableHead>
                      <TableHead>Week end</TableHead>
                      <TableHead>Closed by</TableHead>
                      <TableHead className="text-right">Brands Total</TableHead>
                      <TableHead className="text-right">PH Total</TableHead>
                      <TableHead className="text-right">QG Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {periods.map((p, idx) => (
                      <TableRow
                        key={p.id}
                        data-testid={`history-period-row-${idx}`}
                        onClick={() => navigate(`/history/${p.id}`)}
                        className="cursor-pointer transition-colors duration-150 hover:bg-muted/60"
                      >
                        <TableCell className="font-medium">{formatPeriodDate(p.week_start)}</TableCell>
                        <TableCell className="font-medium">{formatPeriodDate(p.week_end)}</TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="font-normal">
                            {p.closed_by === "manual" ? "Manual reset" : "Automatic (Tuesday)"}
                          </Badge>
                        </TableCell>
                        <TableCell
                          className={cn(
                            "text-right font-mono tabular-nums",
                            p.brands_total < 0 ? "text-red-600" : "text-foreground"
                          )}
                        >
                          {fmtCurrency(p.brands_total)}
                        </TableCell>
                        <TableCell
                          className={cn(
                            "text-right font-mono tabular-nums",
                            p.ph_total < 0 ? "text-red-600" : "text-foreground"
                          )}
                        >
                          {fmtCurrency(p.ph_total)}
                        </TableCell>
                        <TableCell
                          className={cn(
                            "text-right font-mono tabular-nums",
                            p.qg_total < 0 ? "text-red-600" : "text-foreground"
                          )}
                        >
                          {fmtCurrency(p.qg_total)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
